import { useMemo } from 'react';

import { monorepoRoutePaths, routePaths as adminRoutePaths } from '@apps/admin/route.paths';
import type { DashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import type { GetTranslation, RoutesContextProps } from '@shared/core';
import { useRouterHelper, useTranslation } from '@shared/core';
import { Bell, CustomCard, Email, Emails, Invitation, SaveTheDate, ThankYou, WelcomeSign } from '@withjoy/joykit/icons';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';

import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import type { ProductRowItemProps } from '../../PrimaryDashboardContent/ProductRowItem';
import type { AdminDashboardV2Translations } from '@locales/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHasCashFunds } from '@apps/admin/routes/CashFunds/shared/hooks/useHasCashFunds';
import { useSmartRouting } from '@shared/hooks/useSmartRouting';

const getCardTilesByCategory = (args: {
  routeHelpers: RoutesContextProps;
  t2: GetTranslation<AdminDashboardV2Translations<string>>;
  telemetry: DashboardTelemetry;
  printThankYouCategoryEnabled?: boolean;
  contextualPath: ReturnType<typeof useSmartRouting> | null;
}): ProductRowItemProps[] => {
  const { routeHelpers, t2, telemetry, printThankYouCategoryEnabled, contextualPath } = args;

  const { invites: invitesTranslations } = t2('sections');
  const invitesStrings = invitesTranslations;

  const dayOfStationeryLink = {
    id: 'stationery-dayOfStationery',
    title: invitesStrings.children.dayOfStationery.title,
    icon: WelcomeSign,
    hideDropdownLabel: true,
    tileBehavior: 'openDropdown' as const,
    onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'dayOfStationery', section: fromDropdown ? 'Messaging' : '', categoryArg }),
    subItems: [
      {
        title: invitesStrings.children.dayOfStationery.children.signs.title,
        path: routeHelpers.buildPath('/cards/wedding/sign'),
        pathIsInJoyWeb: true,
        onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'dayOfStationery.signs', section: fromDropdown ? 'Messaging' : '', categoryArg }),
      },
      {
        title: invitesStrings.children.dayOfStationery.children.menus.title,
        path: routeHelpers.buildPath('/cards/wedding/menu'),
        pathIsInJoyWeb: true,
        onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'dayOfStationery.menus', section: fromDropdown ? 'Messaging' : '', categoryArg }),
      },
      {
        title: invitesStrings.children.dayOfStationery.children.napkins.title,
        path: routeHelpers.buildPath('/cards/wedding/napkin'),
        pathIsInJoyWeb: true,
        onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'dayOfStationery.napkins', section: fromDropdown ? 'Messaging' : '', categoryArg }),
      },
    ],
  };

  const links = [
    {
      id: 'stationery-saveTheDates',
      title: invitesStrings.children.saveTheDates.title,
      icon: SaveTheDate,
      hideDropdownLabel: true,
      tileBehavior: 'openDropdown' as const,
      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'saveTheDates', section: fromDropdown ? 'Messaging' : '', categoryArg }),
      subItems: [
        {
          title: invitesStrings.children.saveTheDates.children.print.title,
          path: routeHelpers.buildPath('/cards/wedding/save-the-date'),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'saveTheDates.paper', section: fromDropdown ? 'Messaging' : '', categoryArg }),
        },
        {
          // TODO: Perform in-app navigation when we push new ecard editing experience to users
          // https://withjoy.atlassian.net/browse/PRNT-843
          title: invitesStrings.children.saveTheDates.children.digital.title,
          path: routeHelpers.buildPath(monorepoRoutePaths.eCardSaveTheDate),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'saveTheDates.digital', section: fromDropdown ? 'Messaging' : '', categoryArg }),
          telemetryName: monorepoRoutePaths.eCardSaveTheDate,
        },
      ],
    },
    {
      id: 'stationery-invitations',
      title: invitesStrings.children.invitations.title,
      icon: Invitation,
      hideDropdownLabel: true,
      tileBehavior: 'openDropdown' as const,
      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations', section: fromDropdown ? 'Messaging' : '', categoryArg }),
      subItems: [
        {
          title: invitesStrings.children.invitations.children.print.title,
          path: routeHelpers.buildPath('/cards/wedding/invitation'),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations.paper', section: fromDropdown ? 'Messaging' : '', categoryArg }),
        },
        {
          // TODO: Perform in-app navigation when we push new ecard editing experience to users
          // https://withjoy.atlassian.net/browse/PRNT-843
          title: invitesStrings.children.invitations.children.digital.title,
          path: routeHelpers.buildPath(monorepoRoutePaths.eCardInvitations),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations.digital', section: fromDropdown ? 'Messaging' : '', categoryArg }),
          telemetryName: monorepoRoutePaths.eCardInvitations,
        },
      ],
    },
    dayOfStationeryLink,
    ...(printThankYouCategoryEnabled
      ? [
          {
            id: 'stationery-thankYou',
            title: invitesStrings.children.thankYou.title,
            icon: ThankYou,
            hideDropdownLabel: true,
            path: routeHelpers.buildPath('/cards/wedding/thank-you'),
            pathIsInJoyWeb: true,
            onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations.thankYou', section: fromDropdown ? 'Messaging' : '', categoryArg }),
          },
        ]
      : []),
  ];

  const contextualLinks = [
    {
      id: 'stationery-saveTheDates-contextual',
      title: invitesStrings.children.saveTheDates.title,
      icon: SaveTheDate,
      hideDropdownLabel: true,
      path: contextualPath?.saveTheDate ?? undefined,
      pathIsInJoyWeb: true,
      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'saveTheDates.paper', section: fromDropdown ? 'Messaging' : '', categoryArg }),
    },
    {
      id: 'stationery-invitations-contextual',
      title: invitesStrings.children.invitations.title,
      icon: Invitation,
      hideDropdownLabel: true,
      path: contextualPath?.invitation ?? undefined,
      pathIsInJoyWeb: true,
      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations.paper', section: fromDropdown ? 'Messaging' : '', categoryArg }),
    },
    dayOfStationeryLink,
    ...(printThankYouCategoryEnabled
      ? [
          {
            id: 'stationery-thankYou-contextual',
            title: invitesStrings.children.thankYou.title,
            icon: ThankYou,
            hideDropdownLabel: true,
            path: contextualPath?.thankYou ?? undefined,
            pathIsInJoyWeb: true,
            onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations.thankYou', section: fromDropdown ? 'Messaging' : '', categoryArg }),
          },
        ]
      : []),
  ];

  return [...(contextualPath ? contextualLinks : links)];
};

export const useInvitationsProductRow = (args: {
  productVerticals: ProductVerticals;
  printThankYouCategoryEnabled: boolean;
  hotelBlockReminderEnabled: boolean;
  enableRsvpReminderMessage: boolean;
  enableContactCollectionMessage: boolean;
  isSmsChannelEnabled: boolean;
  eventId?: string;
}): ProductRow => {
  const { productVerticals, printThankYouCategoryEnabled, hotelBlockReminderEnabled, enableRsvpReminderMessage, enableContactCollectionMessage, isSmsChannelEnabled } = args;
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const { t2, tq } = useTranslation('adminDashboardV2');
  const { eventInfo } = useEventInfo();

  const useContextualLinks = eventInfo?.featureFlags?.includes('useModernEcards');
  const smartRoutingResult = useSmartRouting({ routeHelpers, skip: !useContextualLinks });
  const contextualPath = useContextualLinks && !smartRoutingResult.isLoading ? smartRoutingResult : null;

  const { visible: showCashFunds, hasNewPill: cashFundsHasNewPill } = useHasCashFunds(eventInfo?.eventId);
  const isCashFundMessagingEnabled = useProFeature(EventFeature.cashFundMessaging);

  const invitationsProductRow = useMemo<ProductRow>(() => {
    const { invites: invitesTranslations } = t2('sections');
    const invitesStrings = invitesTranslations;

    const emailTile: ProductRowItemProps =
      hotelBlockReminderEnabled || enableRsvpReminderMessage || isSmsChannelEnabled
        ? {
            id: 'stationery-messaging',
            title: invitesStrings.children.messaging.title,
            icon: Email,
            hideDropdownLabel: true,
            tileBehavior: 'openDropdown',
            onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'messaging', section: fromDropdown ? 'Messaging' : '', categoryArg }),
            subItems: [
              // -- Track & Send --
              {
                title: tq`Track & Send`,
                path: routeHelpers.buildPath('/messaging'),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'tracking', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: '/messaging',
              },
              // -- Text Blasts, Messages & Collect Contacts --
              ...(isSmsChannelEnabled
                ? [
                    {
                      title: tq`Text Blast`,
                      hasDivider: true,
                      hasNewPill: true,
                      path: routeHelpers.buildPath('/text-blast/create'),
                      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'textblast', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                      telemetryName: '/text-blast/create',
                    },
                  ]
                : []),
              {
                title: invitesStrings.children.messaging.children.general.title,
                hasDivider: !isSmsChannelEnabled,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=generic&fromDashboard=true'),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'generalmessage', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/generalmessage',
              },
              ...(enableContactCollectionMessage
                ? [
                    {
                      title: tq`Collect Contacts`,
                      path: routeHelpers.buildPath('/collect-contacts/create'),
                      onClick: (categoryArg?: string, fromDropdown?: boolean) =>
                        telemetry.openPage({ label: 'collectcontacts', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                      telemetryName: '/collect-contacts/create',
                    },
                  ]
                : []),
              // -- Reminders --
              ...(enableRsvpReminderMessage
                ? [
                    {
                      title: invitesStrings.children.messaging.children.rsvp.title,
                      hasDivider: true,
                      path: routeHelpers.buildPath('/send-rsvp-reminder'),
                      onClick: (categoryArg?: string, fromDropdown?: boolean) =>
                        telemetry.openPage({ label: 'rsvpreminder', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                      telemetryName: monorepoRoutePaths.emailCreate + '/rsvpreminder',
                    },
                  ]
                : []),
              ...(hotelBlockReminderEnabled
                ? [
                    {
                      title: invitesStrings.children.messaging.children.hotel.title,
                      hasDivider: !enableRsvpReminderMessage,
                      path: routeHelpers.buildPath('/hotel-reminder'),
                      onClick: (categoryArg?: string, fromDropdown?: boolean) =>
                        telemetry.openPage({ label: 'hotelreminder', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                      telemetryName: monorepoRoutePaths.emailCreate + '/hotelreminder',
                    },
                  ]
                : []),
              // -- Collect Funds & Cards --
              ...(showCashFunds
                ? [
                    {
                      title: tq`Collect Funds`,
                      hasDivider: true,
                      path: routeHelpers.buildPath(adminRoutePaths.cashfunds.path),
                      pathIsInJoyWeb: true,
                      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'cashFunds', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                      telemetryName: adminRoutePaths.cashfunds.path,
                      hasNewPill: cashFundsHasNewPill,
                    },
                  ]
                : isCashFundMessagingEnabled
                  ? [
                      {
                        title: tq`Request Funds`,
                        hasDivider: true,
                        path: routeHelpers.buildPath('/message/general/create?draftType=cashFund&fromDashboard=true'),
                        onClick: (categoryArg?: string, fromDropdown?: boolean) =>
                          telemetry.openPage({ label: 'requestfunds', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                        telemetryName: '/message/general/create/requestfunds',
                      },
                    ]
                  : []),
              {
                title: invitesStrings.children.customCards.title,
                hasDivider: !showCashFunds && !isCashFundMessagingEnabled,
                path: routeHelpers.buildPath(monorepoRoutePaths.eCardCustom),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'customCards', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.eCardCustom,
              },
            ],
          }
        : {
            id: 'stationery-emails',
            title: invitesStrings.children.emails.title,
            icon: Emails,
            tileBehavior: 'openDropdown',
            onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'emails', section: fromDropdown ? 'Messaging' : '', categoryArg }),
            subItems: [
              {
                title: invitesStrings.children.emails.children.reminder.title,
                icon: Bell,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=reminder&fromDashboard=true'),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'reminderemail', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/reminderemail',
              },
              {
                title: invitesStrings.children.emails.children.general.title,
                icon: Email,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=generic&fromDashboard=true'),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'generalmessage', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/generalmessage',
              },
              {
                title: invitesStrings.children.customCards.title,
                icon: CustomCard,
                path: routeHelpers.buildPath(monorepoRoutePaths.eCardCustom),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'customCards', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.eCardCustom,
              },
            ],
          };

    return {
      title: invitesStrings.title,
      isEnabled: !productVerticals.optOutOfInvites,
      children: [
        ...getCardTilesByCategory({
          routeHelpers,
          t2,
          telemetry,
          printThankYouCategoryEnabled,
          contextualPath,
        }),
        emailTile,
      ],
    };
  }, [
    t2,
    tq,
    hotelBlockReminderEnabled,
    enableRsvpReminderMessage,
    enableContactCollectionMessage,
    routeHelpers,
    productVerticals.optOutOfInvites,
    telemetry,
    printThankYouCategoryEnabled,
    contextualPath,
    showCashFunds,
    cashFundsHasNewPill,
    isCashFundMessagingEnabled,
    isSmsChannelEnabled,
  ]);

  return invitationsProductRow;
};
